import { useMutation, useQueryClient } from "react-query"
import { useMarina } from "src/contexts/MarinaContext"

import {
  completeJob,
  holdJob,
  reopenJob,
  resumeJob,
  startJob,
} from "src/api/Service/Jobs"

import { useToast } from "src/hooks/use_toast"

const useJobTransition = ({ jobId, workOrderSequentialId }) => {
  const { marinaSlug } = useMarina()
  const showToast = useToast()
  const queryClient = useQueryClient()

  const invalidate = () => {
    queryClient.invalidateQueries([
      "workOrder",
      marinaSlug,
      workOrderSequentialId,
    ])
    queryClient.invalidateQueries(["jobActivity", marinaSlug, jobId])
  }

  const handleError = (action) => (error) => {
    const message =
      error?.response?.data?.errors?.[0] ||
      error?.message ||
      `Could not ${action} this job.`
    showToast(message, { type: "error" })
  }

  const startMutation = useMutation(
    () => startJob(marinaSlug, workOrderSequentialId, jobId),
    { onSuccess: invalidate, onError: handleError("start") }
  )

  const completeMutation = useMutation(
    () => completeJob(marinaSlug, workOrderSequentialId, jobId),
    { onSuccess: invalidate, onError: handleError("complete") }
  )

  const reopenMutation = useMutation(
    () => reopenJob(marinaSlug, workOrderSequentialId, jobId),
    {
      onSuccess: () => {
        invalidate()
        queryClient.invalidateQueries(["jobLineItems", marinaSlug, jobId])
        queryClient.invalidateQueries([
          "workOrderInvoices",
          marinaSlug,
          workOrderSequentialId,
        ])
        showToast("Job reopened.", { type: "success" })
      },
      onError: handleError("reopen"),
    }
  )

  const holdMutation = useMutation(
    ({ holdReason }) =>
      holdJob(marinaSlug, workOrderSequentialId, jobId, holdReason),
    {
      onSuccess: () => {
        invalidate()
        showToast("Job placed on hold.", { type: "success" })
      },
      onError: handleError("put on hold"),
    }
  )

  const resumeMutation = useMutation(
    () => resumeJob(marinaSlug, workOrderSequentialId, jobId),
    { onSuccess: invalidate, onError: handleError("resume") }
  )

  return {
    start: () => startMutation.mutate(),
    complete: () => completeMutation.mutate(),
    reopen: (options = {}) => reopenMutation.mutate(undefined, options),
    hold: (holdReason, options = {}) =>
      holdMutation.mutate({ holdReason }, options),
    resume: () => resumeMutation.mutate(),
    isTransitioning:
      startMutation.isLoading ||
      completeMutation.isLoading ||
      reopenMutation.isLoading ||
      holdMutation.isLoading ||
      resumeMutation.isLoading,
  }
}

export default useJobTransition
